<script lang="ts" setup>
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import {useRouter} from 'vue-router'
import SupplierForm from '@/components/features/suppliers/SupplierForm.vue'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import {useSupplierForm} from '@/composables/useSupplierForm'
import PricingForm from '@/components/features/suppliers/PricingForm.vue'
import {onMounted} from 'vue'
import {useSupplierStore} from '@/stores/supplier-store'
import {addSupplierPricingToSupplier, deleteSupplierPricing, updateSupplierPricing} from '@/api/suppliers'

const supplierStore = useSupplierStore()
const router = useRouter()
const {
  values,
  addressFields,
  newAddress,
  removeAddress,
  setErrors,
  formType,
  showToast,
  loading
} = useSupplierForm('view')

const supplierId = router.currentRoute.value.params.id as string

const handleEditSupplier = () => {
  router.push(`/suppliers/${supplierId}/edit`)
}

interface PricingField {
  quality: string
  priceType: 0
  costPerUnitCurrency: number
  costPerUnitAmount: number
  startDate: Date
}

const preparePricingData = (field: PricingField, pricingType: number) => {
  if (pricingType === 2 || pricingType === 3) {
    return {
      pricingType,
      priceType: field.priceType,
      costPerUnitCurrency: field.costPerUnitCurrency,
      costPerUnitAmount: field.costPerUnitAmount,
      startDate: field.startDate
    }
  }

  return {
    qualityId: field.quality,
    pricingType,
    priceType: field.priceType,
    costPerUnitCurrency: field.costPerUnitCurrency,
    costPerUnitAmount: field.costPerUnitAmount,
    startDate: field.startDate
  }
}

const handlePricingSave = async (field: PricingField, pricingType: number) => {
  const data = preparePricingData(field, pricingType)
  if(data.qualityId == "")
  {
  showToast('error', 'Select Quality before proceeding')
  return false
  }

 else
 {
   try {
     await addSupplierPricingToSupplier(supplierId, data)
     showToast('success', 'Pricing Added', 'You have successfully added a new pricing.')
     await supplierStore.categorizeSupplierPricing(supplierId)
    } catch (error) {
      console.error(error)
      showToast('error', 'An error occurred!', error)
    } finally {
      loading.value = false
    }
    return true
  }
}

const handlePricingUpdate = async (
  field: PricingField,
  pricingType: number,
  supplierPricingId: string
) => {
  const data = preparePricingData(field, pricingType)
  try {
    await updateSupplierPricing(data, supplierId, supplierPricingId)
    showToast('success', 'Pricing Updated', 'You have successfully updated the pricing.')
    await supplierStore.categorizeSupplierPricing(supplierId)
  } catch (error) {
    console.error(error)
    showToast('error', 'An error occurred!', 'Unable to update the pricing')
  } finally {
    loading.value = false
  }
}

const handleSaveKnittingPrice = (item: any) => {
  return handlePricingSave(item, 0)
}

const handleUpdateKnittingPrice = (index: number, supplierPricingId: string) => {
  const knitingField = supplierStore.knitingFields[index]
  handlePricingUpdate(knitingField, 0, supplierPricingId)
}

const handleFinishingPrice = (item: any) => {
  return handlePricingSave(item, 1)
}

const handleUpdateFinishingPrice = (index: number, supplierPricingId: string) => {
  const finishingField = supplierStore.finishingFields[index]
  handlePricingUpdate(finishingField, 1, supplierPricingId)
}

const handleSaveTestingPrice = (item: any) => {
  return handlePricingSave(item, 2)
}

const handleUpdateTestingPrice = (index: number, supplierPricingId: string) => {
  const testingField = supplierStore.testingFields[index]
  handlePricingUpdate(testingField, 2, supplierPricingId)
}

const handleQualityCheckPrice = (item: any) => {
  return handlePricingSave(item, 3)
}

const handleUpdateQualityCheckPrice = (index: number, supplierPricingId: string) => {
  const qualityCheckField = supplierStore.qualityCheckFields[index]
  handlePricingUpdate(qualityCheckField, 3, supplierPricingId)
}

const handleDeletePricing = async (supplierPricingId: string) => {
  try {
    await deleteSupplierPricing(supplierId, supplierPricingId)
    showToast('success', 'Pricing Deleted', 'You have successfully deleted the pricing.')

    // Refresh the supplier data to update the UI
    await supplierStore.categorizeSupplierPricing(supplierId)
  } catch (error) {
    console.error(error)
    showToast('error', 'An error occurred!', 'Unable to delete the pricing')
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await supplierStore.categorizeSupplierPricing(supplierId)
})
</script>

<template>
  <PageHeader
    :title="values?.name"
    buttonText="Edit Supplier"
    buttonIcon="basil:edit-solid"
    :buttonClick="handleEditSupplier"
  />
  <TabView class="bg-white rounded-md pb-2">
    <TabPanel header="Supplier Details">
      <SupplierForm
        :type="formType"
        :values="values"
        :onSubmit="handleEditSupplier"
        :addressFields="addressFields"
        :newAddress="newAddress"
        :removeAddress="removeAddress"
        :setErrors="setErrors"
        :showToast="showToast"
      />
    </TabPanel>
    <TabPanel header="Pricing Section">
      <PricingForm
        icon="ph:yarn-fill"
        priceName="Knitting Price"
        :list="supplierStore.knitingFields"
        :save="handleSaveKnittingPrice"
        :update="handleUpdateKnittingPrice"
        :delete="handleDeletePricing"
        :edit="true"
        :setErrors="setErrors"
        sectionType="knitting"
        />
      <PricingForm
        icon="ph:scan-fill"
        priceName="Finishing Pricing"
        :list="supplierStore.finishingFields"
        :save="handleFinishingPrice"
        :update="handleUpdateFinishingPrice"
        :delete="handleDeletePricing"
        :edit="true"
        sectionType="finishing"
      />
      <PricingForm
        icon="ph:fire-fill"
        priceName="FR Testing Pricing"
        :list="supplierStore.testingFields"
        :save="handleSaveTestingPrice"
        :update="handleUpdateTestingPrice"
        :delete="handleDeletePricing"
        :edit="true"
        sectionType="testing"
      />
      <PricingForm
          icon="octicon:check-circle-fill-12"
          priceName="Quality Check Pricing"
          :list="supplierStore.qualityCheckFields"
          :save="handleQualityCheckPrice"
          :update="handleUpdateQualityCheckPrice"
          :delete="handleDeletePricing"
          :edit="true"
          sectionType="qualityCheck"
      />
    </TabPanel>
  </TabView>
</template>
